import { Form, LoopButton, Modal, Toast, Typography } from '@loophealth/loop-ui-web-library';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { saveRequiredUserAttributes } from '../../../../adapters';
import { UserFillingGraphic } from '../../../../assets/img';
import { fetchUserPolicyList } from '../../../../redux/slices/PolicyListSlice';
import WithThemeProvider from '../../../../theme/WithThemeProvider';
import { formatFullName } from '../../../../utils/common';
import { MARITAL_STATUS_OPTIONS } from '../../../../utils/data';
import { selectUserAttributes, selectUserData } from '../../../pages/LoginContainer/redux/slices/UserSlice/selectors';
import { getUserAttributes } from '../../../pages/LoginContainer/redux/slices/UserSlice/thunks';
import {
    StyledUserAttributesBody,
    StyledUserAttributesContent,
    StyledUserAttributesImage,
    StyledUserAttributesItem,
    StyledUserAttributesModal
} from './styles';

const UserAttributesModal: React.FC = () => {
    const toast = Toast.useToast();
    const dispatch = useDispatch();
    const user = useSelector(selectUserData).data;
    const userAttributes = useSelector(selectUserAttributes);
    const [isVisible, setIsVisible] = React.useState(false);
    const [maritalStatus, setMaritalStatus] = useState(() => userAttributes?.data?.maritalStatus ?? '');
    const [isLoading, setIsLoading] = React.useState(false);
    const isFetched = useRef(false);

    const userId = user?.userId;
    const userName = formatFullName(user?.firstName, user?.lastName);
    const isSubmitEnabled = !!maritalStatus;

    useEffect(() => {
        if (!isFetched.current) {
            if (userAttributes?.data) return;
            dispatch(getUserAttributes({ userId }));
            isFetched.current = true;
        } else if (!userAttributes?.loading && !userAttributes?.data && !userAttributes?.error) {
            setIsVisible(true);
        } else if (userAttributes.error) {
            toast?.error(userAttributes.error?.message || 'Something went wrong');
        }
    }, [userAttributes]);

    const handleSubmit = async () => {
        setIsLoading(true);
        try {
            await saveRequiredUserAttributes(userId, { maritalStatus });
            dispatch(getUserAttributes({ userId }));
            dispatch(fetchUserPolicyList({ userId }));
            toast?.success('Details updated successfully.');
            setIsVisible(false);
        } catch (e) {
            toast?.error((e as Error).message ?? 'Something went wrong');
        }
        setIsLoading(false);
    };

    return (
        <Modal
            isVisible={isVisible}
            setIsVisible={setIsVisible}
            isModalClosable={false}
            closeOnBlur={false}
            disablePadding
        >
            <WithThemeProvider>
                <StyledUserAttributesModal>
                    <StyledUserAttributesImage src={UserFillingGraphic} alt="User Attributes" />
                    <StyledUserAttributesBody>
                        <StyledUserAttributesContent>
                            <StyledUserAttributesItem>
                                <Typography variant="title2" weight="medium">
                                    Hey {userName}! 👋
                                </Typography>
                                <Typography variant="medium" color="secondary">
                                    <Typography variant="medium" weight="medium">
                                        You&apos;re Almost There!&nbsp;
                                    </Typography>
                                    Let’s quickly confirm one last detail, so we can unlock the best-fit policy options
                                    tailored just for you and your family.
                                </Typography>
                            </StyledUserAttributesItem>
                            <StyledUserAttributesItem>
                                <Typography variant="small" weight="medium">
                                    Marital Status
                                </Typography>
                                <Form.Dropdown
                                    listItems={MARITAL_STATUS_OPTIONS}
                                    selectedItem={maritalStatus}
                                    placeholder="Select your marital status"
                                    onClick={setMaritalStatus}
                                />
                            </StyledUserAttributesItem>
                        </StyledUserAttributesContent>
                        <StyledUserAttributesItem>
                            <LoopButton
                                onClick={handleSubmit}
                                variant={isSubmitEnabled ? 'filled' : 'disabled'}
                                isLoading={isLoading || userAttributes?.loading}
                            >
                                Confirm & Proceed
                            </LoopButton>
                        </StyledUserAttributesItem>
                    </StyledUserAttributesBody>
                </StyledUserAttributesModal>
            </WithThemeProvider>
        </Modal>
    );
};

export default UserAttributesModal;
